/**
 * Fase 5 — Kurs-veiviser steg 5: Publisering + oppsummering.
 * Visning på kompetanseheving.no + pris + avmelding + no-show-gebyr + lagring.
 */
import { useEffect, useState, useMemo } from 'react';
import { Globe, Tag, DollarSign, UserX, Eye, Info, Calendar, MapPin, Users, Sparkles, Loader2, Plus, X as XIcon, Search } from 'lucide-react';
import { authenticatedFetch } from '../../../lib/auth';
import type { CourseWizardData } from '../NewCourseInstanceWizard';

interface CategorySuggestion {
  category: string;
  score: number;
  matched: string[];
}

interface CategoryRow {
  id: string;
  slug: string;
  display_name: string;
}

export interface CoursePublishStepProps {
  data: CourseWizardData;
  patch: (updates: Partial<CourseWizardData>) => void;
  examSetTitle?: string;
}

export default function CoursePublishStep({ data, patch, examSetTitle }: CoursePublishStepProps) {
  const [suggestions, setSuggestions] = useState<CategorySuggestion[] | null>(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestionError, setSuggestionError] = useState<string | null>(null);
  const [allCategories, setAllCategories] = useState<CategoryRow[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Last alle synkroniserte kompetanseheving.no-kategorier (public endpoint, cachet 10 min).
  useEffect(() => {
    (async () => {
      setLoadingCategories(true);
      try {
        const r = await fetch('/api/categories/list');
        const j = await r.json();
        if (j.success && Array.isArray(j.categories)) {
          setAllCategories(j.categories);
        }
      } catch { /* fallthrough — brukeren kan fortsatt skrive fritekst */ }
      finally { setLoadingCategories(false); }
    })();
  }, []);

  const fetchSuggestions = async () => {
    if (!data.exam_set_id) return;
    setLoadingSuggestions(true);
    setSuggestionError(null);
    try {
      const r = await authenticatedFetch('/api/categories/suggest', {
        method: 'POST',
        body: JSON.stringify({ exam_set_id: data.exam_set_id }),
      });
      const j = await r.json();
      if (!r.ok || !j.success) throw new Error(j.error || 'Kunne ikke hente forslag');
      const already = new Set(data.categories.map(c => c.toLowerCase()));
      const fresh = (j.suggestions || []).filter((s: CategorySuggestion) => !already.has(s.category.toLowerCase()));
      setSuggestions(fresh);
      if (fresh.length === 0 && (j.suggestions || []).length > 0) {
        setSuggestionError('Alle foreslåtte kategorier er allerede lagt til.');
      } else if ((j.suggestions || []).length === 0) {
        setSuggestionError('Fant ingen gode kategori-forslag — studieplanen mangler ESCO-yrkesmålgruppe eller passer ikke med eksisterende kategorier. Velg manuelt fra lista nedenfor.');
      }
    } catch (err: unknown) {
      setSuggestionError(err instanceof Error ? err.message : 'Ukjent feil');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const addCategory = (category: string) => {
    if (data.categories.includes(category)) return;
    patch({ categories: [...data.categories, category] });
    setSuggestions(prev => prev ? prev.filter(s => s.category !== category) : prev);
    setCategorySearch('');
  };

  const removeCategory = (category: string) => {
    patch({ categories: data.categories.filter(c => c !== category) });
  };

  // Filtrert dropdown-liste: fjern allerede valgte + match søk
  const filteredCategoryOptions = useMemo(() => {
    const selected = new Set(data.categories.map(c => c.toLowerCase()));
    const q = categorySearch.trim().toLowerCase();
    return allCategories
      .filter(c => !selected.has(c.display_name.toLowerCase()))
      .filter(c => !q || c.display_name.toLowerCase().includes(q));
  }, [allCategories, data.categories, categorySearch]);

  return (
    <div className="space-y-5">
      {/* Oppsummering */}
      <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4">
        <h4 className="font-semibold text-emerald-900 mb-2">Oppsummering før lagring</h4>
        <dl className="text-sm space-y-1 text-emerald-900">
          <SummaryRow icon={<Calendar className="w-4 h-4" />} label="Prøvesett">{examSetTitle || data.exam_set_id}</SummaryRow>
          <SummaryRow icon={<Users className="w-4 h-4" />} label="Plasser">{data.max_candidates ?? 'Ubegrenset'}</SummaryRow>
          <SummaryRow icon={<Calendar className="w-4 h-4" />} label="Kursperiode">
            {data.start_date}{data.end_date ? ` → ${data.end_date}` : ''}
          </SummaryRow>
          <SummaryRow icon={<Calendar className="w-4 h-4" />} label="Eksamen">
            {data.exam_date && data.exam_time ? `${data.exam_date} kl. ${data.exam_time}` : '(ikke satt)'}
          </SummaryRow>
          <SummaryRow icon={<MapPin className="w-4 h-4" />} label="Lokasjon">
            {data.location_type === 'physical' ? (data.location || 'Fysisk — adresse mangler')
              : data.location_type === 'digital' ? (data.meeting_url || 'Digital — URL mangler')
              : `${data.location || 'Fysisk ikke satt'} + ${data.meeting_url || 'Digital ikke satt'}`}
          </SummaryRow>
          <SummaryRow icon={<Users className="w-4 h-4" />} label="Sensorer">
            {data.selected_sensors.length > 0
              ? data.selected_sensors.map((s) => s.full_name).join(', ')
              : <span className="text-amber-700">Ingen tildelt (sensur kan ikke gjennomføres)</span>}
          </SummaryRow>
        </dl>
      </div>

      {/* Publisering på kompetanseheving.no */}
      <div className="rounded-lg border border-kf-gray-200 p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <Globe className="w-5 h-5 text-kf-blue-600 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <label htmlFor="publish-toggle" className="block text-sm font-medium text-kf-gray-900">
                Vis på kompetanseheving.no
              </label>
              <p className="text-xs text-kf-gray-600 mt-1">
                Kurset vises offentlig på kompetanseheving.no og åpnes for Vipps-selvpåmelding (hvis fri plass).
                Kan slås av senere.
              </p>
            </div>
          </div>
          <button
            id="publish-toggle"
            type="button"
            role="switch"
            aria-checked={data.visible_on_kompetanseheving}
            onClick={() => patch({ visible_on_kompetanseheving: !data.visible_on_kompetanseheving })}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
              data.visible_on_kompetanseheving ? 'bg-kf-blue-600' : 'bg-kf-gray-300'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out ${
                data.visible_on_kompetanseheving ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {data.visible_on_kompetanseheving && (
          <div className="mt-4 pt-4 border-t border-kf-gray-200 space-y-4">
            {/* Kategorier */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-kf-gray-700">
                  <Tag size={14} className="inline mr-1" />
                  Kategorier
                </label>
                {data.exam_set_id && (
                  <button
                    type="button"
                    onClick={fetchSuggestions}
                    disabled={loadingSuggestions}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded border border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 disabled:opacity-50"
                    title="Foreslår kategorier basert på studieplanens yrkesmålgruppe (uten KI-bruk — regelbasert matching mot ESCO-yrker)"
                  >
                    {loadingSuggestions ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Sparkles className="w-3 h-3" />
                    )}
                    Foreslå kategorier
                  </button>
                )}
              </div>

              {/* Valgte kategorier som chips */}
              {data.categories.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {data.categories.map((cat) => (
                    <span
                      key={cat}
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-kf-blue-100 text-kf-blue-800 border border-kf-blue-200"
                    >
                      {cat}
                      <button
                        type="button"
                        onClick={() => removeCategory(cat)}
                        className="text-kf-blue-600 hover:text-kf-blue-900"
                        title="Fjern"
                      >
                        <XIcon className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Søkbar dropdown over faktiske kompetanseheving.no-kategorier */}
              <div className="relative">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="w-4 h-4 text-kf-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="text"
                      value={categorySearch}
                      onChange={(e) => { setCategorySearch(e.target.value); setDropdownOpen(true); }}
                      onFocus={() => setDropdownOpen(true)}
                      onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
                      placeholder={loadingCategories ? 'Laster kategorier…' : 'Søk eller velg kategori fra kompetanseheving.no…'}
                      disabled={loadingCategories}
                      className="w-full pl-8 pr-3 py-2 text-sm border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
                    />
                  </div>
                </div>
                {dropdownOpen && filteredCategoryOptions.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto bg-white border border-kf-gray-300 rounded-lg shadow-lg">
                    {filteredCategoryOptions.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { addCategory(cat.display_name); setDropdownOpen(false); }}
                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-kf-blue-50 flex items-center gap-2"
                      >
                        <Plus className="w-3.5 h-3.5 text-kf-blue-600 shrink-0" />
                        {cat.display_name}
                      </button>
                    ))}
                  </div>
                )}
                {dropdownOpen && !loadingCategories && filteredCategoryOptions.length === 0 && categorySearch.trim() && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-kf-gray-300 rounded-lg shadow-lg px-3 py-2 text-xs text-kf-gray-600">
                    Ingen kategorier matcher «{categorySearch}». Kategorilista synkroniseres
                    nattlig fra kompetanseheving.no — be systemadmin legge til kategorien der
                    hvis den mangler.
                  </div>
                )}
              </div>
              <p className="text-xs text-kf-gray-500 mt-1">
                Kategoriene er hentet direkte fra kompetanseheving.no slik at kurset
                dukker opp i riktig filter. Klikk «Foreslå kategorier» for forslag basert
                på studieplanens yrkesmålgruppe.
              </p>

              {/* Forslag-visning */}
              {suggestions && suggestions.length > 0 && (
                <div className="mt-2 rounded-md border border-purple-200 bg-purple-50 p-2">
                  <p className="text-xs font-medium text-purple-900 mb-1.5 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    Foreslåtte kategorier — klikk for å legge til
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {suggestions.map((s) => (
                      <button
                        key={s.category}
                        type="button"
                        onClick={() => addCategory(s.category)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-white hover:bg-purple-100 text-purple-800 border border-purple-300"
                        title={`Match-score: ${s.score}. Trigget av yrkesroller: ${s.matched.join(', ')}`}
                      >
                        <Plus className="w-3 h-3" />
                        {s.category}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-purple-700 mt-1.5">
                    Regelbasert matching mot ESCO-yrkeskatalog — ingen KI brukt.
                  </p>
                </div>
              )}
              {suggestionError && (
                <p className="text-xs text-amber-700 mt-1.5">{suggestionError}</p>
              )}
            </div>

            {/* Vis antall påmeldte */}
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-2">
                <Eye className="w-4 h-4 text-kf-gray-600 mt-0.5" />
                <div>
                  <label htmlFor="show-count-toggle" className="text-sm font-medium text-kf-gray-900">
                    Vis antall påmeldte offentlig
                  </label>
                  <p className="text-xs text-kf-gray-600">
                    Default av. Kan skape mer attraksjon men også avskrekke.
                  </p>
                </div>
              </div>
              <button
                id="show-count-toggle"
                type="button"
                onClick={() => patch({ show_registered_count: !data.show_registered_count })}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full ${
                  data.show_registered_count ? 'bg-kf-blue-600' : 'bg-kf-gray-300'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${
                    data.show_registered_count ? 'translate-x-4' : 'translate-x-0'
                  }`}
                  style={{ marginTop: '2px', marginLeft: '2px' }}
                />
              </button>
            </div>

            {/* Pris */}
            <div>
              <label className="block text-sm font-medium text-kf-gray-700 mb-1">
                <DollarSign size={14} className="inline mr-1" />
                Pris per plass (NOK)
              </label>
              <input
                type="number"
                value={data.registration_fee_nok}
                onChange={(e) => patch({ registration_fee_nok: parseInt(e.target.value) || 0 })}
                min={0}
                className="w-40 px-3 py-2 text-sm border border-kf-gray-300 rounded-lg"
              />
            </div>

            {/* Selvavmelding */}
            <div>
              <label className="block text-sm font-medium text-kf-gray-700 mb-1">Selvavmeldingsfrist (dager før kursstart)</label>
              <input
                type="number"
                value={data.self_cancel_deadline_days}
                onChange={(e) => patch({ self_cancel_deadline_days: parseInt(e.target.value) || 30 })}
                min={0}
                className="w-32 px-3 py-2 text-sm border border-kf-gray-300 rounded-lg"
              />
              <p className="text-xs text-kf-gray-500 mt-1">
                Kandidater kan selvavmelde seg uten gebyr frem til så mange dager før kursstart. Standard: 30 dager.
              </p>
            </div>

            {/* No-show */}
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
              <UserX className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <label className="flex items-center gap-2 text-sm font-medium text-amber-900">
                  <input
                    type="checkbox"
                    checked={data.no_show_fee_active}
                    onChange={(e) => patch({ no_show_fee_active: e.target.checked })}
                  />
                  Krev gebyr ved manglende oppmøte (no-show)
                </label>
                <p className="text-xs text-amber-800 mt-1">
                  Pris-beløpet brukes som no-show-gebyr. Kandidater varsles om denne regelen ved påmelding.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-md bg-kf-blue-50 border border-kf-blue-200 p-3 text-sm text-kf-blue-900 flex items-start gap-2">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <span>
          Kurset opprettes som <strong>aktivt</strong>. Hvis "Vis på kompetanseheving.no" er av, er kurset synlig kun i
          administrasjonen og kan vedlikeholdes med manuell påmelding. Du kan slå på publisering senere.
        </span>
      </div>
    </div>
  );
}

function SummaryRow({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2">
      <span className="shrink-0 mt-0.5">{icon}</span>
      <span className="font-medium w-28 shrink-0">{label}:</span>
      <span className="flex-1 min-w-0">{children}</span>
    </div>
  );
}
